import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Pencil, Plus, Search, Star, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { AppShell } from '@/components/layout/AppShell';
import { AdminSectionTabs } from '@/components/admin/AdminSectionTabs';
import { Button } from '@/components/ui/Button';
import { StatCard } from '@/components/ui/StatCard';
import { adminArticlesService } from '@/services/admin-articles.service';
import { ApiError } from '@/services/http';
import { LEARNS_TOPICS, type LearnsTopicId } from '@/constants/learns-data';
import { READING_LEVELS, type ReadingLevel } from '@/store/learning.store';
import { cn } from '@/lib/cn';
import type { ArticleStatus, ArticleView } from '@/types/api';

/**
 * AdminArticlesPage — `/admin/articles`. Table of every article in the DB
 * (drafts included), with status / topic / level filters and free-text
 * search. Mirrors the layout of the Books admin dashboard so the tab
 * switch feels like the same surface.
 *
 * URL is the source of truth for filters + page — matches the Books
 * convention (`?status=pending&topic=history&page=2`).
 */
const PAGE_SIZE = 20;

type StatusFilter = ArticleStatus | 'all';

interface Filters {
  status: StatusFilter;
  topic: LearnsTopicId | 'all';
  level: ReadingLevel | 'all';
  q: string;
}

const EMPTY_FILTERS: Filters = { status: 'all', topic: 'all', level: 'all', q: '' };

function readFilters(params: URLSearchParams): Filters {
  const raw = {
    status: params.get('status') ?? 'all',
    topic: params.get('topic') ?? 'all',
    level: params.get('level') ?? 'all',
    q: params.get('q') ?? '',
  };
  return {
    status: (raw.status === 'pending' || raw.status === 'live'
      ? raw.status
      : 'all') as StatusFilter,
    topic: (LEARNS_TOPICS.some((t) => t.id === raw.topic) ? raw.topic : 'all') as Filters['topic'],
    level: ((READING_LEVELS as readonly string[]).includes(raw.level)
      ? raw.level
      : 'all') as Filters['level'],
    q: raw.q,
  };
}

function writeParams(
  filters: Filters,
  page: number,
  setSearchParams: (next: URLSearchParams) => void,
) {
  const next = new URLSearchParams();
  if (filters.status !== 'all') next.set('status', filters.status);
  if (filters.topic !== 'all') next.set('topic', filters.topic);
  if (filters.level !== 'all') next.set('level', filters.level);
  if (filters.q.trim()) next.set('q', filters.q.trim());
  if (page > 1) next.set('page', String(page));
  setSearchParams(next);
}

export function AdminArticlesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => readFilters(searchParams), [searchParams]);
  const page = Math.max(1, Number(searchParams.get('page') ?? '1') || 1);

  const [articles, setArticles] = useState<ArticleView[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  // Local search-input draft, separate from the committed `q` filter in the
  // URL. Committed only when the admin submits — Enter or the Search button —
  // never on a timer.
  const [searchDraft, setSearchDraft] = useState(filters.q);
  useEffect(() => setSearchDraft(filters.q), [filters.q]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const offset = (page - 1) * PAGE_SIZE;
    (async () => {
      try {
        const result = await adminArticlesService.list({
          status: filters.status === 'all' ? undefined : filters.status,
          topic: filters.topic === 'all' ? undefined : [filters.topic],
          level: filters.level === 'all' ? undefined : [filters.level],
          q: filters.q || undefined,
          limit: PAGE_SIZE,
          offset,
        });
        if (cancelled) return;
        setArticles(result.articles);
        setTotalCount(result.totalCount);
      } catch (err) {
        if (cancelled) return;
        toast.error(err instanceof ApiError ? err.message : 'Could not load articles.');
        setArticles([]);
        setTotalCount(0);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [filters.status, filters.topic, filters.level, filters.q, page, refreshTick]);

  const applyFilters = useCallback(
    (next: Filters) => writeParams(next, 1, setSearchParams),
    [setSearchParams],
  );

  const handleDelete = async (article: ArticleView) => {
    const ok = window.confirm(`Delete "${article.title}"? This cannot be undone.`);
    if (!ok) return;
    try {
      await adminArticlesService.remove(article.id);
      toast.success(`Deleted "${article.title}".`);
      setRefreshTick((t) => t + 1);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  const lastPage = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const goToPage = (next: number) => {
    const clamped = Math.max(1, Math.min(lastPage, next));
    if (clamped === page) return;
    writeParams(filters, clamped, setSearchParams);
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const liveCount = articles.filter((a) => a.status === 'live').length;
  const pendingCount = articles.filter((a) => a.status === 'pending').length;
  const featuredTitle = articles.find((a) => a.isFeatured)?.title ?? '—';

  return (
    <AppShell mode="parent">
      <div className="flex flex-col gap-8">
        {/* Section tabs */}
        <AdminSectionTabs />

        {/* Sub-header */}
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="or-kicker text-or-orange-600">Admin · Ollie Learns</div>
            <h1 className="mt-1 font-display text-[22px] font-bold text-or-slate-900 sm:text-[26px]">
              Article Management
            </h1>
          </div>
          <Button variant="primary" size="md" onClick={() => navigate('/admin/articles/new')}>
            <Plus className="h-4 w-4" aria-hidden />
            New article
          </Button>
        </div>

        {/* Stats strip */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            label="Total articles"
            value={totalCount.toLocaleString()}
            delta={loading ? 'Refreshing…' : 'Live + pending'}
            tone="default"
          />
          <StatCard
            label="Live on this page"
            value={liveCount.toLocaleString()}
            delta={`${liveCount} of ${articles.length}`}
            tone="success"
          />
          <StatCard
            label="Drafts on this page"
            value={pendingCount.toLocaleString()}
            delta={pendingCount === 0 ? 'No drafts here' : 'Still pending review'}
            tone="warning"
          />
          <StatCard
            label="Featured"
            value={featuredTitle === '—' ? '—' : '1'}
            delta={featuredTitle === '—' ? 'Not on this page' : featuredTitle}
            tone="default"
          />
        </div>

        {/* Filter toolbar */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Search */}
          <form
            className="flex items-center gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              applyFilters({ ...filters, q: searchDraft });
            }}
          >
            <div className="relative">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-or-slate-400"
                aria-hidden
              />
              <input
                type="text"
                placeholder="Search title or summary"
                aria-label="Search articles by title or summary"
                value={searchDraft}
                onChange={(e) => setSearchDraft(e.target.value)}
                className="h-9 w-64 rounded-full border border-or-slate-200 bg-white pl-9 pr-3 text-[13px] text-or-slate-900 placeholder:text-or-slate-400 focus:border-or-orange-400 focus:outline-none focus:ring-2 focus:ring-or-orange-400/30"
              />
            </div>
            <button
              type="submit"
              className="h-9 shrink-0 rounded-full bg-or-orange-500 px-4 text-[13px] font-semibold text-white transition hover:bg-or-orange-600 focus:outline-none focus:ring-2 focus:ring-or-orange-400/40"
            >
              Search
            </button>
          </form>

          {/* Status */}
          <FilterSelect
            label="Status"
            value={filters.status}
            onChange={(value) => applyFilters({ ...filters, status: value as StatusFilter })}
            options={[
              { value: 'all', label: 'All statuses' },
              { value: 'live', label: 'Live' },
              { value: 'pending', label: 'Pending' },
            ]}
          />
          {/* Topic */}
          <FilterSelect
            label="Topic"
            value={filters.topic}
            onChange={(value) => applyFilters({ ...filters, topic: value as Filters['topic'] })}
            options={[
              { value: 'all', label: 'All topics' },
              ...LEARNS_TOPICS.map((t) => ({ value: t.id, label: t.label })),
            ]}
          />
          {/* Level */}
          <FilterSelect
            label="Level"
            value={filters.level}
            onChange={(value) => applyFilters({ ...filters, level: value as Filters['level'] })}
            options={[
              { value: 'all', label: 'All levels' },
              ...READING_LEVELS.map((l) => ({ value: l, label: `Grade ${l}` })),
            ]}
          />

          {(filters.status !== 'all' ||
            filters.topic !== 'all' ||
            filters.level !== 'all' ||
            filters.q) && (
            <Button variant="ghost" size="sm" onClick={() => applyFilters(EMPTY_FILTERS)}>
              Clear filters
            </Button>
          )}
        </div>

        {/* Table */}
        <div className="overflow-hidden rounded-2xl border border-or-slate-200 bg-white">
          <table className="w-full text-left text-[13px]">
            <thead className="bg-or-slate-50 text-[11px] uppercase tracking-[0.08em] text-or-slate-500">
              <tr>
                <th className="px-4 py-3">Title</th>
                <th className="px-4 py-3">Topic</th>
                <th className="px-4 py-3">Level</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Updated</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && articles.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-or-slate-500">
                    Loading…
                  </td>
                </tr>
              ) : articles.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-or-slate-500">
                    No articles match these filters yet.
                  </td>
                </tr>
              ) : (
                articles.map((a) => (
                  <ArticleRow key={a.id} article={a} onDelete={() => handleDelete(a)} />
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalCount > PAGE_SIZE ? (
          <div className="flex items-center justify-between text-[12px] text-or-slate-500">
            <span>
              Page {page} of {lastPage} · {totalCount.toLocaleString()} articles
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => goToPage(page - 1)}
                disabled={page <= 1}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => goToPage(page + 1)}
                disabled={page >= lastPage}
              >
                Next
              </Button>
            </div>
          </div>
        ) : null}
      </div>
    </AppShell>
  );
}

// -- Helpers -------------------------------------------------------------

function ArticleRow({ article, onDelete }: { article: ArticleView; onDelete: () => void }) {
  const topic = LEARNS_TOPICS.find((t) => t.id === article.topic);
  return (
    <tr className="border-t border-or-slate-100 align-middle">
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          {article.isFeatured ? (
            <Star
              className="h-3.5 w-3.5 text-or-yellow-400"
              aria-label="Currently featured"
              fill="currentColor"
            />
          ) : null}
          <Link
            to={`/admin/articles/${article.id}`}
            className="font-semibold text-or-slate-900 hover:text-or-orange-700"
          >
            {article.title}
          </Link>
        </div>
        <div className="truncate text-[12px] text-or-slate-500">
          /learns/articles/{article.slug}
        </div>
      </td>
      <td className="px-4 py-3">
        {topic ? (
          <span
            className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold"
            style={{ backgroundColor: topic.soft, color: topic.accent }}
          >
            {topic.label}
          </span>
        ) : (
          <span className="text-or-slate-500">{article.topic}</span>
        )}
      </td>
      <td className="px-4 py-3 text-or-slate-700">Grade {article.level}</td>
      <td className="px-4 py-3">
        <StatusPill status={article.status} />
      </td>
      <td className="px-4 py-3 text-or-slate-500">
        {new Date(article.updatedAt).toLocaleDateString()}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-end gap-1.5">
          <Link
            to={`/admin/articles/${article.id}`}
            className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-or-slate-200 bg-white text-or-slate-600 hover:bg-or-slate-50"
            aria-label={`Edit ${article.title}`}
          >
            <Pencil className="h-3.5 w-3.5" aria-hidden />
          </Link>
          <button
            type="button"
            onClick={onDelete}
            className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-or-rose-100 bg-white text-or-rose-700 hover:bg-or-rose-100"
            aria-label={`Delete ${article.title}`}
          >
            <Trash2 className="h-3.5 w-3.5" aria-hidden />
          </button>
        </div>
      </td>
    </tr>
  );
}

function StatusPill({ status }: { status: string }) {
  const isLive = status === 'live';
  return (
    <span
      className={cn(
        'inline-flex rounded-full px-2.5 py-0.5 text-[11px] font-semibold',
        isLive ? 'bg-or-green-100 text-or-green-700' : 'bg-or-yellow-200 text-or-slate-800',
      )}
    >
      {isLive ? 'Live' : 'Pending'}
    </span>
  );
}

interface FilterSelectProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
}

function FilterSelect({ label, value, onChange, options }: FilterSelectProps) {
  return (
    <label className="inline-flex items-center gap-2 text-[12px] text-or-slate-500">
      <span className="sr-only">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 rounded-full border border-or-slate-200 bg-white px-3 text-[13px] font-semibold text-or-slate-700 focus:border-or-orange-400 focus:outline-none focus:ring-2 focus:ring-or-orange-400/30"
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </label>
  );
}
